import jsPDF from 'jspdf'

// Executive Advisory Report PDF exporter (POC: Cover + TOC + Executive Summary).
//
// Input shape:
//   {
//     client:        { name, industry, subIndustry, ... }
//     context:       the aggregated context used for AI generation (for facts)
//     draft:         AI-generated Executive Summary content (Sonnet)
//     review:        AI QA output with confidence scores (Opus)
//     brand:         { firmName, logoUrl, primaryHex, accentHex, trademarkLine }
//   }

const DEFAULT_BRAND = {
  firmName: 'Sidewalk Security Advisors',
  logoUrl: null,
  primaryHex: '0D1B2A',
  accentHex: 'C9A84C',
  trademarkLine: null
}

const MARGIN = 20
const GRAY = [71, 85, 105]
const LIGHT_GRAY = [148, 163, 184]
const MUTED_BG = [241, 245, 249]
const WHITE = [255, 255, 255]
const SEVERITY_COLORS = {
  high: [220, 38, 38],
  medium: [234, 88, 12],
  low: [202, 138, 4]
}

// --- Low-confidence threshold: anything below this gets a visual marker ---
const LOW_CONFIDENCE_THRESHOLD = 70

export async function exportAdvisoryReport({
  client,
  context,
  draft,
  review,
  brand = DEFAULT_BRAND
}) {
  const pdf = new jsPDF({ unit: 'mm', format: 'letter' })
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const contentWidth = pageWidth - MARGIN * 2

  const primary = hexToRgb(brand.primaryHex || DEFAULT_BRAND.primaryHex)
  const accent = hexToRgb(brand.accentHex || DEFAULT_BRAND.accentHex)

  const state = {
    pdf,
    pageWidth,
    pageHeight,
    contentWidth,
    primary,
    accent,
    brand: { ...DEFAULT_BRAND, ...brand },
    y: MARGIN,
    pageNumber: 1
  }

  // ─── Page 1: Cover ───
  drawCover(state, client)

  // ─── Page 2: Table of Contents ───
  addPage(state)
  drawTableOfContents(state, draft)

  // ─── Page 3+: Executive Summary ───
  addPage(state)
  drawExecutiveSummary(state, draft, review, context)

  // Footer on every page
  const totalPages = pdf.internal.getNumberOfPages()
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i)
    drawFooter(state, i, totalPages)
  }

  const filename = `${(client?.name || 'client').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-advisory-report.pdf`
  pdf.save(filename)
}

// ============================================================
// Page builders
// ============================================================

function drawCover(state, client) {
  const { pdf, pageWidth, pageHeight, primary, accent, brand } = state

  // Full-bleed primary band across top third
  pdf.setFillColor(...primary)
  pdf.rect(0, 0, pageWidth, pageHeight * 0.38, 'F')

  // Accent stripe
  pdf.setFillColor(...accent)
  pdf.rect(0, pageHeight * 0.38, pageWidth, 2, 'F')

  // Firm name top-left on primary band
  pdf.setTextColor(...WHITE)
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(11)
  pdf.text((brand.firmName || DEFAULT_BRAND.firmName).toUpperCase(), MARGIN, 25)

  // Report label
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(9)
  pdf.setTextColor(255, 255, 255, 0.8)
  pdf.text('EXECUTIVE ADVISORY REPORT', MARGIN, 32)

  // Client name — large, centered vertically in primary band
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(28)
  pdf.setTextColor(...WHITE)
  const title = client?.name || 'Client Organization'
  const titleLines = pdf.splitTextToSize(title, pageWidth - MARGIN * 2)
  let titleY = 65
  titleLines.forEach(line => {
    pdf.text(line, MARGIN, titleY)
    titleY += 12
  })

  // Subtitle
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(12)
  pdf.setTextColor(...WHITE)
  pdf.text('Security Program Assessment & Strategic Roadmap', MARGIN, titleY + 2)

  // Body below the band
  pdf.setTextColor(...GRAY)
  const bodyY = pageHeight * 0.5
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(10)
  pdf.text('PREPARED FOR', MARGIN, bodyY)
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(12)
  pdf.setTextColor(30, 41, 59)
  pdf.text(client?.name || 'Client Organization', MARGIN, bodyY + 6)
  if (client?.industry) {
    pdf.setFontSize(10)
    pdf.setTextColor(...LIGHT_GRAY)
    pdf.text(client.industry, MARGIN, bodyY + 12)
  }

  // Date
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(10)
  pdf.setTextColor(...GRAY)
  pdf.text('REPORT DATE', MARGIN, bodyY + 25)
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(12)
  pdf.setTextColor(30, 41, 59)
  pdf.text(
    new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    MARGIN,
    bodyY + 31
  )

  // Prepared by
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(10)
  pdf.setTextColor(...GRAY)
  pdf.text('PREPARED BY', MARGIN, bodyY + 44)
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(12)
  pdf.setTextColor(30, 41, 59)
  pdf.text(brand.firmName || DEFAULT_BRAND.firmName, MARGIN, bodyY + 50)

  // Confidentiality notice at bottom
  pdf.setFont('helvetica', 'italic')
  pdf.setFontSize(8)
  pdf.setTextColor(...LIGHT_GRAY)
  pdf.text(
    'Confidential and proprietary. Contains sensitive assessment findings.',
    MARGIN,
    pageHeight - 25
  )
}

function drawTableOfContents(state, draft) {
  const { pdf, contentWidth } = state
  sectionHeader(state, 'Table of Contents')

  const sections = [
    { num: '01', title: 'Executive Summary', status: draft ? 'included' : 'placeholder' },
    { num: '02', title: 'Business Goals & Engagement Context', status: 'placeholder' },
    { num: '03', title: 'Top Threats & Breach Analysis', status: 'placeholder' },
    { num: '04', title: 'Engagement Findings', status: 'placeholder' },
    { num: '05', title: 'Compliance & Maturity Coverage', status: 'placeholder' },
    { num: '06', title: 'Key Strengths', status: 'placeholder' },
    { num: '07', title: 'Key Risks & Weaknesses', status: 'placeholder' },
    { num: '08', title: 'Strategic Opportunities', status: 'placeholder' },
    { num: '09', title: 'Cyber Liability Insurance Review', status: 'placeholder' },
    { num: '10', title: 'Strategic Roadmap Overview', status: 'placeholder' },
    { num: '11', title: 'Next Steps for Leadership', status: 'placeholder' },
    { num: 'A', title: 'Appendix A — Acronyms', status: 'placeholder' },
    { num: 'B', title: 'Appendix B — Document Review List', status: 'placeholder' },
    { num: 'C', title: 'Appendix C — Detailed Threat Profile', status: 'placeholder' },
    { num: 'D', title: 'Appendix D — Risk Analysis', status: 'placeholder' }
  ]

  pdf.setFontSize(10)
  sections.forEach(s => {
    ensureSpace(state, 9)
    // Number column
    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(...state.primary)
    pdf.text(s.num, MARGIN, state.y)

    // Title
    pdf.setFont('helvetica', 'normal')
    pdf.setTextColor(30, 41, 59)
    pdf.text(s.title, MARGIN + 12, state.y)

    // Status tag
    if (s.status === 'placeholder') {
      pdf.setFont('helvetica', 'italic')
      pdf.setFontSize(8)
      pdf.setTextColor(...LIGHT_GRAY)
      pdf.text('[placeholder]', MARGIN + contentWidth - 20, state.y)
      pdf.setFontSize(10)
    } else {
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(8)
      pdf.setTextColor(34, 139, 34)
      pdf.text('INCLUDED', MARGIN + contentWidth - 16, state.y)
      pdf.setFontSize(10)
    }
    state.y += 8
  })

  state.y += 5
  calloutBox(state, {
    title: 'About this report',
    body: 'This document reflects the current engagement state. Sections marked [placeholder] will be populated as underlying data is gathered and approved. Sections marked LOW CONFIDENCE flag areas requiring advisor review before delivery.'
  })
}

function drawExecutiveSummary(state, draft, review, context) {
  const { pdf, contentWidth } = state
  sectionHeader(state, 'Executive Summary')

  const confidence = review?.sectionConfidence || {}

  // Headline
  if (draft?.headline && draft.headline !== 'DATA_NOT_PROVIDED') {
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(14)
    pdf.setTextColor(...state.primary)
    const headlineLines = pdf.splitTextToSize(draft.headline, contentWidth)
    ensureSpace(state, headlineLines.length * 7 + 2)
    headlineLines.forEach(line => {
      pdf.text(line, MARGIN, state.y)
      state.y += 7
    })
    state.y += 4
    confidenceTag(state, confidence.headline, 'Headline')
  } else {
    placeholderBlock(state, 'Headline not generated', 'Run assessment to generate executive headline.')
  }

  // Program Snapshot (maturity gauge + narrative)
  ensureSpace(state, 60)
  subsectionTitle(state, 'Information Security Program Snapshot')
  const gaugeY = state.y
  drawMaturityGauge(state, context?.scores?.overall?.score || 0, context?.scores?.overall?.maturityLabel || 'Initial')
  // Narrative to the right of gauge
  if (draft?.programSnapshotNarrative && draft.programSnapshotNarrative !== 'DATA_NOT_PROVIDED') {
    const narrativeX = MARGIN + 70
    const narrativeWidth = contentWidth - 70
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(10)
    pdf.setTextColor(...GRAY)
    const lines = pdf.splitTextToSize(draft.programSnapshotNarrative, narrativeWidth)
    let ly = gaugeY + 12
    lines.forEach(line => {
      pdf.text(line, narrativeX, ly)
      ly += 5
    })
  }
  state.y = gaugeY + 55
  confidenceTag(state, confidence.programSnapshotNarrative, 'Snapshot narrative')

  // Top Risks
  ensureSpace(state, 30)
  subsectionTitle(state, 'Top 3 Risks')
  if (Array.isArray(draft?.topRisks) && draft.topRisks.length > 0) {
    draft.topRisks.forEach((risk, idx) => {
      ensureSpace(state, 30)
      riskCard(state, idx + 1, risk)
    })
  } else {
    placeholderBlock(state, 'Risks not generated', 'No top risks returned from analysis.')
  }
  confidenceTag(state, confidence.topRisks, 'Top Risks')

  // Top Opportunities
  ensureSpace(state, 30)
  subsectionTitle(state, 'Top 3 Opportunities')
  if (Array.isArray(draft?.topOpportunities) && draft.topOpportunities.length > 0) {
    draft.topOpportunities.forEach((opp, idx) => {
      ensureSpace(state, 25)
      opportunityCard(state, idx + 1, opp)
    })
  } else {
    placeholderBlock(state, 'Opportunities not generated', 'No top opportunities returned from analysis.')
  }
  confidenceTag(state, confidence.topOpportunities, 'Top Opportunities')

  // Regulatory Alignment
  ensureSpace(state, 40)
  subsectionTitle(state, 'Regulatory Alignment')
  if (draft?.regulatoryAlignment?.narrative && draft.regulatoryAlignment.narrative !== 'DATA_NOT_PROVIDED') {
    paragraph(state, draft.regulatoryAlignment.narrative)
    if (draft.regulatoryAlignment.coverageNotes && draft.regulatoryAlignment.coverageNotes !== 'DATA_NOT_PROVIDED') {
      state.y += 2
      paragraph(state, draft.regulatoryAlignment.coverageNotes, { italic: true })
    }
  } else {
    placeholderBlock(state, 'Regulatory alignment not generated', 'Run assessment with industry and framework data.')
  }
  if (Array.isArray(context?.applicableFrameworks) && context.applicableFrameworks.length > 0) {
    state.y += 4
    frameworkChips(state, context.applicableFrameworks)
  }
  confidenceTag(state, confidence.regulatoryAlignment, 'Regulatory Alignment')

  // Business Goals (inferred, requires admin confirmation)
  ensureSpace(state, 30)
  subsectionTitle(state, 'Business Goals (Inferred)')
  inlineNotice(state, 'Draft — requires admin confirmation before client delivery.')
  if (Array.isArray(draft?.businessGoals) && draft.businessGoals.length > 0) {
    draft.businessGoals.forEach(g => {
      ensureSpace(state, 14)
      businessGoalRow(state, g)
    })
  } else {
    placeholderBlock(state, 'Business goals not inferred', 'Insufficient data to infer business goals.')
  }
  confidenceTag(state, confidence.businessGoals, 'Business Goals')

  // QA summary note at bottom of section
  if (review?.qualityNotes) {
    ensureSpace(state, 30)
    state.y += 6
    calloutBox(state, {
      title: `QA Review — Overall Confidence: ${review.overallConfidence ?? 'N/A'}${typeof review.overallConfidence === 'number' ? '%' : ''}`,
      body: review.qualityNotes,
      tone: review.overallConfidence != null && review.overallConfidence < LOW_CONFIDENCE_THRESHOLD ? 'warning' : 'info'
    })
  }
}

// ============================================================
// Drawing primitives
// ============================================================

function addPage(state) {
  state.pdf.addPage()
  state.pageNumber += 1
  state.y = MARGIN
}

function ensureSpace(state, required) {
  if (state.y + required > state.pageHeight - MARGIN - 12) {
    addPage(state)
  }
}

function sectionHeader(state, title) {
  const { pdf, primary, accent } = state
  pdf.setFillColor(...primary)
  pdf.rect(MARGIN, state.y, 4, 14, 'F')
  pdf.setFillColor(...accent)
  pdf.rect(MARGIN, state.y + 14, 4, 2, 'F')

  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(18)
  pdf.setTextColor(...primary)
  pdf.text(title, MARGIN + 10, state.y + 10)
  state.y += 22
}

function subsectionTitle(state, title) {
  const { pdf, primary } = state
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(12)
  pdf.setTextColor(...primary)
  pdf.text(title.toUpperCase(), MARGIN, state.y)
  state.y += 3
  pdf.setDrawColor(...LIGHT_GRAY)
  pdf.setLineWidth(0.3)
  pdf.line(MARGIN, state.y, MARGIN + state.contentWidth, state.y)
  state.y += 5
}

function paragraph(state, text, { italic = false } = {}) {
  const { pdf, contentWidth } = state
  pdf.setFont('helvetica', italic ? 'italic' : 'normal')
  pdf.setFontSize(10)
  pdf.setTextColor(...GRAY)
  const lines = pdf.splitTextToSize(text, contentWidth)
  ensureSpace(state, lines.length * 5 + 2)
  lines.forEach(line => {
    pdf.text(line, MARGIN, state.y)
    state.y += 5
  })
}

function inlineNotice(state, text) {
  const { pdf, contentWidth } = state
  pdf.setFillColor(254, 243, 199)
  pdf.rect(MARGIN, state.y - 3, contentWidth, 6, 'F')
  pdf.setFont('helvetica', 'italic')
  pdf.setFontSize(8)
  pdf.setTextColor(146, 64, 14)
  pdf.text(text, MARGIN + 2, state.y + 1)
  state.y += 8
}

function placeholderBlock(state, title, body) {
  const { pdf, contentWidth } = state
  const height = 14
  ensureSpace(state, height + 2)
  pdf.setFillColor(...MUTED_BG)
  pdf.rect(MARGIN, state.y, contentWidth, height, 'F')
  pdf.setDrawColor(...LIGHT_GRAY)
  pdf.setLineDashPattern([1, 1], 0)
  pdf.rect(MARGIN, state.y, contentWidth, height)
  pdf.setLineDashPattern([], 0)
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(9)
  pdf.setTextColor(...LIGHT_GRAY)
  pdf.text(`[PLACEHOLDER] ${title}`, MARGIN + 3, state.y + 5)
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(8)
  pdf.text(body, MARGIN + 3, state.y + 10)
  state.y += height + 4
}

function calloutBox(state, { title, body, tone = 'info' }) {
  const { pdf, contentWidth, primary, accent } = state
  const barColor = tone === 'warning' ? [234, 88, 12] : primary
  pdf.setFillColor(...MUTED_BG)
  const titleLines = title ? pdf.splitTextToSize(title, contentWidth - 10) : []
  const bodyLines = pdf.splitTextToSize(body, contentWidth - 10)
  const height = 6 + titleLines.length * 5 + bodyLines.length * 5
  ensureSpace(state, height + 2)
  pdf.rect(MARGIN, state.y, contentWidth, height, 'F')
  pdf.setFillColor(...barColor)
  pdf.rect(MARGIN, state.y, 2, height, 'F')

  let cy = state.y + 5
  if (title) {
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(9)
    pdf.setTextColor(...primary)
    titleLines.forEach(line => {
      pdf.text(line, MARGIN + 6, cy)
      cy += 5
    })
  }
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(9)
  pdf.setTextColor(...GRAY)
  bodyLines.forEach(line => {
    pdf.text(line, MARGIN + 6, cy)
    cy += 5
  })
  state.y += height + 4
}

function drawMaturityGauge(state, score, label) {
  const { pdf, primary } = state
  const cx = MARGIN + 28
  const cy = state.y + 20
  const radius = 20

  // Background ring (light gray)
  pdf.setDrawColor(226, 232, 240)
  pdf.setLineWidth(4)
  pdf.circle(cx, cy, radius, 'S')

  // Foreground arc — jsPDF lacks a native arc helper, so we approximate with
  // segmented small circles. A rough dotted look is acceptable for the POC.
  const color = colorForScore(score)
  pdf.setFillColor(...color)
  const pct = Math.max(0, Math.min(100, score)) / 100
  const segments = 60
  const filled = Math.round(segments * pct)
  for (let i = 0; i < filled; i++) {
    const angle = (-Math.PI / 2) + (i / segments) * (2 * Math.PI)
    const px = cx + Math.cos(angle) * radius
    const py = cy + Math.sin(angle) * radius
    pdf.circle(px, py, 0.9, 'F')
  }

  // Score in center
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(22)
  pdf.setTextColor(...primary)
  const scoreText = String(Math.round(score || 0))
  const scoreWidth = pdf.getTextWidth(scoreText)
  pdf.text(scoreText, cx - scoreWidth / 2, cy + 2)

  // Label below
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(8)
  pdf.setTextColor(...GRAY)
  const labelWidth = pdf.getTextWidth(label || '')
  pdf.text(label || '', cx - labelWidth / 2, cy + 8)

  pdf.setFontSize(7)
  pdf.setTextColor(...LIGHT_GRAY)
  pdf.text('OVERALL MATURITY', cx - pdf.getTextWidth('OVERALL MATURITY') / 2, cy - 12)
}

function riskCard(state, idx, risk) {
  const { pdf, contentWidth, primary } = state
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(10)
  pdf.setTextColor(...primary)
  const title = `${idx}. ${risk?.title || 'Untitled risk'}`
  pdf.text(title, MARGIN, state.y)
  state.y += 5

  if (risk?.rationale) {
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(9)
    pdf.setTextColor(...GRAY)
    const lines = pdf.splitTextToSize(risk.rationale, contentWidth - 4)
    lines.forEach(line => {
      pdf.text(line, MARGIN + 4, state.y)
      state.y += 4.5
    })
  }

  if (risk?.businessImpact) {
    state.y += 1
    pdf.setFillColor(254, 226, 226)
    const impactLines = pdf.splitTextToSize(`Business Impact: ${risk.businessImpact}`, contentWidth - 8)
    const boxH = 3 + impactLines.length * 4.5
    pdf.rect(MARGIN + 4, state.y - 1, contentWidth - 4, boxH, 'F')
    pdf.setFont('helvetica', 'italic')
    pdf.setFontSize(8)
    pdf.setTextColor(153, 27, 27)
    let iy = state.y + 2.5
    impactLines.forEach(line => {
      pdf.text(line, MARGIN + 6, iy)
      iy += 4.5
    })
    state.y += boxH + 1
  }
  state.y += 3
}

function opportunityCard(state, idx, opp) {
  const { pdf, contentWidth, accent } = state
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(10)
  pdf.setTextColor(...accent)
  pdf.text(`${idx}. ${opp?.title || 'Untitled opportunity'}`, MARGIN, state.y)
  state.y += 5

  if (opp?.rationale) {
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(9)
    pdf.setTextColor(...GRAY)
    const lines = pdf.splitTextToSize(opp.rationale, contentWidth - 4)
    lines.forEach(line => {
      pdf.text(line, MARGIN + 4, state.y)
      state.y += 4.5
    })
  }

  if (opp?.value) {
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(8)
    pdf.setTextColor(34, 139, 34)
    const lines = pdf.splitTextToSize(`Value: ${opp.value}`, contentWidth - 4)
    lines.forEach(line => {
      pdf.text(line, MARGIN + 4, state.y)
      state.y += 4
    })
  }
  state.y += 3
}

function frameworkChips(state, frameworks) {
  const { pdf, contentWidth, primary } = state
  let x = MARGIN
  const chipHeight = 6
  const spacing = 3
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(8)
  frameworks.slice(0, 10).forEach(fw => {
    const label = typeof fw === 'string' ? fw : (fw.name || fw.id || '')
    if (!label) return
    const chipWidth = pdf.getTextWidth(label) + 6
    if (x + chipWidth > MARGIN + contentWidth) {
      state.y += chipHeight + 2
      x = MARGIN
    }
    pdf.setFillColor(...state.primary)
    pdf.roundedRect(x, state.y - 4, chipWidth, chipHeight, 1, 1, 'F')
    pdf.setTextColor(...WHITE)
    pdf.text(label, x + 3, state.y)
    x += chipWidth + spacing
  })
  state.y += chipHeight + 2
  pdf.setTextColor(...GRAY)
}

function businessGoalRow(state, goal) {
  const { pdf, contentWidth } = state
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(10)
  pdf.setTextColor(30, 41, 59)
  pdf.text(`• ${goal?.goal || 'Unnamed goal'}`, MARGIN, state.y)
  // Confidence tag
  const conf = (goal?.confidence || '').toLowerCase()
  if (conf) {
    const color = conf === 'high' ? [34, 139, 34] : conf === 'medium' ? [234, 88, 12] : [202, 138, 4]
    const tagText = conf.toUpperCase()
    const tagX = MARGIN + contentWidth - pdf.getTextWidth(tagText) - 6
    pdf.setFillColor(...color)
    pdf.roundedRect(tagX - 2, state.y - 3.5, pdf.getTextWidth(tagText) + 4, 5, 1, 1, 'F')
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(7)
    pdf.setTextColor(...WHITE)
    pdf.text(tagText, tagX, state.y)
  }
  state.y += 4.5
  if (goal?.evidence) {
    pdf.setFont('helvetica', 'italic')
    pdf.setFontSize(8)
    pdf.setTextColor(...LIGHT_GRAY)
    const lines = pdf.splitTextToSize(`Evidence: ${goal.evidence}`, contentWidth - 6)
    lines.forEach(line => {
      pdf.text(line, MARGIN + 4, state.y)
      state.y += 4
    })
  }
  state.y += 1
}

function confidenceTag(state, value, label) {
  if (typeof value !== 'number') return
  if (value >= LOW_CONFIDENCE_THRESHOLD) return
  const { pdf, contentWidth } = state
  ensureSpace(state, 8)
  pdf.setFillColor(254, 243, 199)
  pdf.rect(MARGIN, state.y - 3, contentWidth, 5, 'F')
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(7)
  pdf.setTextColor(146, 64, 14)
  pdf.text(`⚠ LOW CONFIDENCE (${value}%) — ${label}: flagged for advisor review`, MARGIN + 2, state.y + 0.5)
  state.y += 6
}

function drawFooter(state, pageNum, totalPages) {
  const { pdf, pageWidth, pageHeight, brand } = state
  const y = pageHeight - 10
  pdf.setDrawColor(...LIGHT_GRAY)
  pdf.setLineWidth(0.2)
  pdf.line(MARGIN, y - 3, pageWidth - MARGIN, y - 3)

  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(7)
  pdf.setTextColor(...LIGHT_GRAY)
  const trademark = brand.trademarkLine || `Prepared by ${brand.firmName || DEFAULT_BRAND.firmName}`
  pdf.text(trademark, MARGIN, y)
  pdf.text(`Page ${pageNum} of ${totalPages}`, pageWidth - MARGIN - pdf.getTextWidth(`Page ${pageNum} of ${totalPages}`), y)
}

// ============================================================
// Utilities
// ============================================================

function hexToRgb(hex) {
  const clean = String(hex || '').replace(/^#/, '')
  if (clean.length !== 6) return [13, 27, 42]
  const r = parseInt(clean.slice(0, 2), 16)
  const g = parseInt(clean.slice(2, 4), 16)
  const b = parseInt(clean.slice(4, 6), 16)
  if ([r, g, b].some(n => Number.isNaN(n))) return [13, 27, 42]
  return [r, g, b]
}

function colorForScore(score) {
  if (score >= 80) return [34, 139, 34]
  if (score >= 60) return [59, 130, 246]
  if (score >= 40) return [234, 179, 8]
  if (score >= 20) return [249, 115, 22]
  return [220, 38, 38]
}
